/**
 * Format a stored `HH:mm` (24-hour) time-of-day string for display as a
 * 12-hour clock value. The DB and `<input type="time">` keep the canonical
 * 24-hour string; this helper is for read paths only.
 *
 * Feature request: dreeves@siteld.com asked that controller program start
 * times render in standard 12-hour AM/PM format rather than military.
 *
 * Locale aware. Spanish locales will pick up the corresponding lowercase
 * `a. m.` / `p. m.` from Intl; English locales get `AM` / `PM`. Pass the
 * current i18n language (e.g. `i18n.language`).
 *
 * Tolerant on malformed input: returns the original string if it can't be
 * parsed, so a partial render is preferred to a thrown error.
 */
export function formatTimeOfDay(hhmm: string | null | undefined, locale: string): string {
  if (!hhmm || typeof hhmm !== 'string') return '';
  const match = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  if (!match) return hhmm;
  const h = Number(match[1]);
  const m = Number(match[2]);
  if (!Number.isFinite(h) || !Number.isFinite(m) || h < 0 || h > 23 || m < 0 || m > 59) {
    return hhmm;
  }
  // Use a fixed reference date — only the hour/minute matter. The Intl path
  // produces the right localized AM/PM token for the current language.
  const d = new Date(2000, 0, 1, h, m);
  return d.toLocaleTimeString(locale, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}
